<template>
  <v-card>
    <v-toolbar>
      <v-toolbar-title>
        Dealer Payouts Earned<span v-if="dealerName">: {{ dealerName }}</span>
        <span v-else-if="loading">: Loading...</span>
      </v-toolbar-title>
      <v-spacer />
      <v-menu offset-y :close-on-content-click="false" min-width="360">
        <template v-slot:activator="{ on: onMenu }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on: onTooltip }">
              <v-btn class="mr-2" icon v-on="{ ...onMenu, ...onTooltip }">
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span>{{ $i18n.translate("Advanced Search") }}</span>
          </v-tooltip>
        </template>
        <v-list>
          <v-list-item-group color="primary">
            <v-subheader>{{ $i18n.translate("Advanced Search") }}</v-subheader>
            <v-list-item selectable>
              <v-list-item-content>
                <v-text-field
                  dense
                  v-model="filters.productKey"
                  :label="$i18n.translate('Model')"
                  clearable
                  @input="onSubmit"
                />
              </v-list-item-content>
            </v-list-item>
            <v-list-item selectable>
              <v-list-item-content>
                <v-text-field
                  dense
                  v-model="filters.serialNumber"
                  :label="$i18n.translate('VIN')"
                  clearable
                  @input="onSubmit"
                />
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-menu>
      <v-tooltip bottom v-if="hasFilters">
        <template v-slot:activator="{ on: onTooltip }">
          <v-btn class="mr-2" icon v-on="onTooltip" @click="onClearSearch">
            <v-icon>mdi-magnify-close</v-icon>
          </v-btn>
        </template>
        <span>{{ $i18n.translate("Clear Search") }}</span>
      </v-tooltip>
      <v-btn
        icon
        title="Export CSV"
        aria-label="Export CSV"
        :disabled="loading || loadError || isExporting || !payouts.length"
        @click="exportDisplayedData"
      >
        <v-icon v-if="!isExporting">mdi-download</v-icon>
        <v-progress-circular v-else indeterminate color="primary" size="20" width="2" />
      </v-btn>
    </v-toolbar>
    <v-card-text>
      <v-alert v-if="exportError" type="error" text>{{ exportError }}</v-alert>
      <v-alert v-if="loadError" type="error" text>
        Unable to load dealer payout details. Please try again.
      </v-alert>
      <v-data-table
        v-else
        :headers="headers"
        :items="payouts"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :no-data-text="noPayoutsMessage"
      >
        <template v-slot:item.createdDate="{ item }">
          {{ item.createdDate | formatDateClient("MM/DD/YYYY hh:mm", selectedClient) }}
        </template>
        <template v-slot:item.payoutAmount="{ item }">
          {{ item.payoutAmount | toCurrency }}
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex/dist/vuex.common.js";

export default {
  name: "DealerDetails",
  data() {
    return {
      loading: true,
      isExporting: false,
      exportError: "",
      dealerPayout: null,
      loadError: false,
      payouts: [],
      total: 0,
      filters: {
        productKey: "",
        serialNumber: ""
      },
      options: {
        itemsPerPage: 10,
        page: 1,
        sortDesc: [true]
      },
      headers: [
        { text: "Date", value: "createdDate" },
        { text: "Promotion", value: "promotion.name" },
        { text: "Model", value: "saleProduct.product.productKey", sortable: false },
        { text: "VIN", value: "saleProduct.serialNumber", sortable: false },
        { text: "Award Vehicle", value: "awardVehicle.name" },
        { text: "Amount", value: "payoutAmount" }
      ]
    };
  },
  computed: {
    ...mapGetters(["selectedParticipant", "selectedClient", "selectedFiscalYear"]),
    hasFilters() {
      return !!(this.filters.productKey?.trim() || this.filters.serialNumber?.trim());
    },
    dealerName() {
      if (!this.dealerPayout) return "";

      return `${this.dealerPayout.name || "N/A"} (${this.dealerPayout.organizationKey || "N/A"})`;
    },
    noPayoutsMessage() {
      return !this.loading && !this.loadError && !this.payouts.length
        ? "No payouts match the selected period and filters."
        : "";
    }
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    options: {
      handler() {
        this.fetchData();
      },
      deep: true
    },
    selectedFiscalYear() {
      this.fetchData();
    }
  },
  methods: {
    onSubmit() {
      if (this.options.page !== 1) {
        this.options.page = 1;
      } else {
        this.fetchData();
      }
    },
    onClearSearch() {
      this.filters = { productKey: "", serialNumber: "" };
      this.onSubmit();
    },
    getDealerFilters() {
      const filters = { organization: { id: this.$route.query.dealerId } };
      if (this.selectedFiscalYear?.fiscalYearStart) {
        filters.createdAfter = this.selectedFiscalYear.fiscalYearStart;
      }
      if (this.selectedFiscalYear?.fiscalYearEnd) {
        filters.createdBefore = this.$util
          .getDateInClientTimezone(this.selectedFiscalYear.fiscalYearEnd)
          .add(1, "day")
          .toDate();
      }

      return filters;
    },
    getPayoutFilters() {
      const filters = this.getDealerFilters();
      if (this.filters.productKey?.trim()) {
        filters.saleProductProductKey = this.filters.productKey.trim();
      }
      if (this.filters.serialNumber?.trim()) {
        filters.saleProductSerialNumber = this.filters.serialNumber.trim();
      }
      return filters;
    },
    async fetchData() {
      this.loading = true;
      this.loadError = false;
      this.exportError = "";
      this.dealerPayout = null;
      this.payouts = [];
      this.total = 0;

      const filters = this.getPayoutFilters();
      const params = this.$util.buildPaginationParams(this.options);
      params.append("expand", "organization");
      params.append("expand", "promotion");
      params.append("expand", "awardVehicle");
      params.append("expand", "saleProduct.product");

      try {
        const [payoutResponse, organizationResponse] = await Promise.all([
          this.$api.post(`/api/payouts/search?${params.toString()}`, filters),
          this.$api.post("/api/payouts/search/group/byOrganization?size=1&page=0", this.getDealerFilters())
        ]);

        this.payouts = payoutResponse.data.content;
        this.total = payoutResponse.data.totalElements;
        this.dealerPayout = organizationResponse.data.content[0] || null;
      } catch {
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    async exportDisplayedData() {
      this.isExporting = true;
      this.exportError = "";
      try {
        const payouts = await this.$util.fetchPaginatedData("/api/payouts/search", this.getPayoutFilters(), 100, [
          "organization",
          "promotion",
          "awardVehicle",
          "saleProduct.product"
        ]);
        const columns = this.headers.map(header => {
          if (header.value === "createdDate") {
            return {
              ...header,
              format: row => this.$util.formatDateClient(row.createdDate, "MM/DD/YYYY hh:mm", this.selectedClient)
            };
          }
          if (header.value === "payoutAmount") {
            return { ...header, format: row => this.$util.toCurrency(row.payoutAmount) };
          }
          return header;
        });
        this.$export.exportToCsv(`${this.dealerPayout?.organizationKey || "dealer"}_payouts_earned`, columns, payouts);
      } catch (error) {
        this.exportError =
          this.$kawasaki.getApiErrorMessages(error?.response?.data)[0] ||
          error?.message ||
          "Unable to export the report. Please try again.";
      } finally {
        this.isExporting = false;
      }
    }
  }
};
</script>
